/**
 * Pickpocketing — the only theft that happens while the owner is standing
 * there, which makes it the most stealth-shaped thing in the game: it is
 * entirely about not being noticed.
 *
 * Three outcomes, not two. **taken** is silent. **failed** is a miss nobody
 * registers — your hand comes back empty and you can try again, which is what
 * makes a low-percentage attempt tempting rather than suicidal. **fumbled** is
 * the one that costs you: they feel it, and what they do about it depends on
 * what they still have in their pockets (see `robbedReaction`).
 *
 * That last part is the design: steal the radio first and the guard who catches
 * you afterwards has to go and FIND someone. Order of theft is tactics.
 *
 * Pure + headless (rule 4). Randomness is an injected `rng`, matching the
 * convention in combat.ts / lockpick.ts / body.ts.
 */

import { CONFIG } from '../config';
import type { Guard } from './guard';
import type { Item } from './inventory';
import type { Player } from './player';
import { hasComms } from './pockets';

export type StealOutcome = 'taken' | 'failed' | 'fumbled';

/** How bulky a thing is to lift out of someone's pocket unnoticed. */
function bulk(item: Item): number {
  const cfg = CONFIG.pickpocket.bulk;
  switch (item.kind) {
    case 'valuable': return cfg.small;   // a wallet
    case 'key': return cfg.small;        // a keycard
    case 'phone': return cfg.medium;
    case 'radio': return cfg.medium;     // clipped on, and they'd miss it
    case 'junk': return cfg.large;       // a mop is not a pickpocket target
    default: return cfg.medium;
  }
}

/** Is the victim looking roughly at the player? Facing away is the whole art. */
export function facingPlayer(victim: Guard, player: Player): boolean {
  const dx = Math.sign(player.x - victim.x);
  const dy = Math.sign(player.y - victim.y);
  if (dx === 0 && dy === 0) return true;
  // Same half-plane as their facing counts as "looking your way".
  return (dx === victim.facingDx && dy === victim.facingDy)
    || (dx === victim.facingDx && victim.facingDx !== 0)
    || (dy === victim.facingDy && victim.facingDy !== 0);
}

/**
 * Chance of lifting `item` cleanly, 0..1. Everything that makes it harder is
 * something the player controls or can wait out: how alert they are, whether
 * they're facing you, how you're standing, and what you're reaching for.
 */
export function stealChance(player: Player, victim: Guard, item: Item): number {
  const cfg = CONFIG.pickpocket;
  let chance = cfg.base;
  // Their attention. An alert guard is hopeless; an oblivious one is easy.
  chance *= cfg.awareness[victim.state] ?? 1;
  if (facingPlayer(victim, player)) chance *= cfg.facingMult;
  // Your hands and your footing.
  chance *= player.stance === 'crouch' ? cfg.crouchMult
    : player.stance === 'run' ? cfg.runMult : 1;
  if (victim.moveProgress > 0) chance *= cfg.movingMult; // mid-stride, harder to read
  chance *= bulk(item);
  return Math.max(0.02, Math.min(0.97, chance));
}

/**
 * Attempt the lift. On `taken` the item moves from the victim's pockets to the
 * player's loot; on anything else nothing moves. The caller handles the noise,
 * the log line and — for `fumbled` — the reaction.
 */
export function attemptSteal(
  player: Player,
  victim: Guard,
  item: Item,
  rng: () => number = Math.random,
): StealOutcome {
  const idx = victim.pockets.findIndex((it) => it.id === item.id);
  if (idx < 0) return 'failed'; // already gone — someone else's problem
  const chance = stealChance(player, victim, item);
  const roll = rng();
  if (roll < chance) {
    victim.pockets.splice(idx, 1);
    player.dungeonLoot.push(item);
    return 'taken';
  }
  // The band between a clean miss and being felt. A near-miss on an easy mark
  // goes unnoticed; a bad roll on a hard one does not.
  return roll < chance + CONFIG.pickpocket.failBand ? 'failed' : 'fumbled';
}

/** What a robbed victim does about it, decided by what they still carry. */
export type RobbedReaction =
  | { kind: 'radio' }        // calls it in — everyone knows at once
  | { kind: 'phone' }        // calls it in, slower
  | { kind: 'shout' };       // no comms left: has to go and find somebody

export function robbedReaction(victim: Guard): RobbedReaction {
  const comms = hasComms(victim);
  if (comms === 'radio') return { kind: 'radio' };
  if (comms === 'phone') return { kind: 'phone' };
  return { kind: 'shout' };
}
